import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useRouter } from 'next/router'

import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState
} from 'react'

import { CornerDownLeft, Search, SearchX, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ICONS } from '@/lib/icons'
import { rankArticles } from '@/lib/knowledge'
import { useDismiss } from '@/hooks/use-dismiss'
import { Highlight } from '@/components/knowledge/highlight'
import { LISTED_ARTICLES } from '@/constants/articles'
import { CATEGORY_BY_SLUG, TYPE_BY_KEY } from '@/constants/taxonomy'

const POPULAR_SEARCHES = ['API', 'شاپرک', 'استقرار', 'امنیت', 'درس‌آموخته', 'Kafka']

interface HeaderSearchProps {
    shortcut?: boolean
    autoFocus?: boolean
    onNavigate?: () => void
    className?: string
}

export const HeaderSearch: FC<HeaderSearchProps> = ({
    shortcut = false,
    autoFocus = false,
    onNavigate,
    className
}): ReactNode => {
    const router = useRouter()
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const [active, setActive] = useState(-1)
    const rootRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const listId = useId()

    const close = useCallback(() => {
        setOpen(false)
        setActive(-1)
    }, [])

    useDismiss(rootRef, open, close)

    const trimmed = query.trim()
    const hasQuery = trimmed.length >= 2
    const results = useMemo(() => hasQuery ? rankArticles(LISTED_ARTICLES, trimmed).slice(0, 5) : [], [hasQuery, trimmed])
    const optionCount = hasQuery ? results.length + 1 : 0

    useEffect(() => {
        if (autoFocus) inputRef.current?.focus()
    }, [autoFocus])

    useEffect(() => {
        if (!shortcut) return

        const handleKey = (event: globalThis.KeyboardEvent) => {
            if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return

            const target = event.target as HTMLElement | null

            if (target?.closest('input, textarea, select, [contenteditable="true"]')) return

            event.preventDefault()
            inputRef.current?.focus()
        }

        document.addEventListener('keydown', handleKey)

        return () => document.removeEventListener('keydown', handleKey)
    }, [shortcut])

    const navigate = (href: string) => {
        close()
        setQuery('')
        inputRef.current?.blur()
        onNavigate?.()
        router.push(href)
    }

    const submit = (value: string = trimmed) => {
        navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search')
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setOpen(true)
            setActive(index => optionCount ? (index + 1) % optionCount : -1)
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive(index => optionCount ? (index <= 0 ? optionCount - 1 : index - 1) : -1)
        }
        else if (event.key === 'Enter') {
            event.preventDefault()

            if (active >= 0 && active < results.length) navigate(`/knowledge/${results[active].article.slug}`)
            else submit()
        }
        else if (event.key === 'Escape') {
            close()
            inputRef.current?.blur()
        }
    }

    const optionId = (index: number) => `${listId}-option-${index}`

    return (
        <div ref={rootRef} className={cn('relative w-full', className)}>
            <form
                role='search'
                onSubmit={event => {
                    event.preventDefault()
                    submit()
                }}
            >
                <label htmlFor={`${listId}-input`} className='sr-only'>
                    جست‌وجوی دانش
                </label>

                <Search className='pointer-events-none absolute start-3.5 top-1/2 size-5 -translate-y-1/2 text-subtle' aria-hidden='true' />

                <input
                    ref={inputRef}
                    id={`${listId}-input`}
                    type='search'
                    role='combobox'
                    autoComplete='off'
                    aria-expanded={open}
                    aria-controls={`${listId}-list`}
                    aria-activedescendant={active >= 0 ? optionId(active) : undefined}
                    value={query}
                    onChange={event => {
                        setQuery(event.target.value)
                        setOpen(true)
                        setActive(-1)
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder='دانش، مستندات، موضوعات و تجربیات موردنظر خود را جست‌وجو کنید...'
                    className='h-11 w-full rounded-lg border border-transparent bg-secondary/80 ps-11 pe-11 text-sm text-foreground placeholder:text-subtle transition-[background-color,border-color,box-shadow] duration-150 hover:border-input focus-visible:border-primary focus-visible:bg-card focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/15 [&::-webkit-search-cancel-button]:hidden'
                />

                {query ? (
                    <button
                        type='button'
                        onClick={() => {
                            setQuery('')
                            inputRef.current?.focus()
                        }}
                        className='absolute end-2 top-1/2 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-card hover:text-navy'
                        aria-label='پاک کردن عبارت جست‌وجو'
                    >
                        <X className='size-4' aria-hidden='true' />
                    </button>
                ) : shortcut ? (
                    <kbd className='pointer-events-none absolute end-3 top-1/2 hidden h-6 -translate-y-1/2 items-center rounded-md border border-input bg-card px-2 font-mono text-caption text-muted-foreground lg:flex'>
                        /
                    </kbd>
                ) : null}
            </form>

            {open && (
                <div
                    className='absolute inset-x-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-popover p-2 shadow-pop animate-in fade-in slide-in-from-top-1 duration-150'
                >
                    {!hasQuery ? (
                        <div className='flex flex-col gap-3 p-2'>
                            <p className='text-caption font-semibold text-muted-foreground'>
                                جست‌وجوهای پرتکرار همکاران
                            </p>

                            <div className='flex flex-wrap gap-2'>
                                {POPULAR_SEARCHES.map(term => (
                                    <button
                                        key={term}
                                        type='button'
                                        onClick={() => submit(term)}
                                        className='h-8 cursor-pointer rounded-full border border-input bg-card px-3 text-small text-foreground transition-colors hover:border-primary/40 hover:bg-primary-soft hover:text-primary-deep'
                                    >
                                        {term}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <ul id={`${listId}-list`} role='listbox' aria-label='پیشنهادهای جست‌وجو' className='flex flex-col gap-0.5'>
                            {results.length === 0 && (
                                <li className='flex items-center gap-3 px-3 py-3 text-small text-muted-foreground' role='presentation'>
                                    <SearchX className='size-5 text-subtle' aria-hidden='true' />
                                    <span>دانشی با این عنوان پیدا نشد. جست‌وجوی کامل را امتحان کنید.</span>
                                </li>
                            )}

                            {results.map(({ article }, index) => {
                                const Icon = ICONS[TYPE_BY_KEY[article.type].icon]

                                return (
                                    <li
                                        key={article.slug}
                                        id={optionId(index)}
                                        role='option'
                                        aria-selected={active === index}
                                        onMouseEnter={() => setActive(index)}
                                        onMouseDown={event => event.preventDefault()}
                                        onClick={() => navigate(`/knowledge/${article.slug}`)}
                                        className={cn(
                                            'flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors',
                                            active === index ? 'bg-primary-soft' : 'hover:bg-secondary'
                                        )}
                                    >
                                        <span className='mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-card text-primary ring-1 ring-border'>
                                            <Icon className='size-4' aria-hidden='true' />
                                        </span>

                                        <span className='flex min-w-0 flex-col'>
                                            <span className='truncate text-sm font-semibold text-navy'>
                                                <Highlight text={article.title} query={trimmed} />
                                            </span>

                                            <span className='truncate text-caption text-muted-foreground'>
                                                {CATEGORY_BY_SLUG[article.category]?.title} · {TYPE_BY_KEY[article.type].title}
                                            </span>
                                        </span>
                                    </li>
                                )
                            })}

                            <li
                                id={optionId(results.length)}
                                role='option'
                                aria-selected={active === results.length}
                                onMouseEnter={() => setActive(results.length)}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => submit()}
                                className={cn(
                                    'mt-1 flex cursor-pointer items-center justify-between gap-3 rounded-lg border-t border-border px-3 py-2.5 text-sm font-semibold text-primary-strong transition-colors',
                                    active === results.length ? 'bg-primary-soft' : 'hover:bg-secondary'
                                )}
                            >
                                <span className='truncate'>
                                    مشاهده همه نتایج برای «{trimmed}»
                                </span>

                                <CornerDownLeft className='size-4 shrink-0' aria-hidden='true' />
                            </li>
                        </ul>
                    )}
                </div>
            )}
        </div>
    )
}
